import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FaChalkboard, FaMapMarkerAlt, FaVideo } from "react-icons/fa";
import { HiOutlineUser } from "react-icons/hi";
import {
  HiOutlineCalendar,
  HiOutlineChartBar,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiStar,
} from "react-icons/hi2";
import { Link } from "react-router-dom";
import Badge from "../../components/Badge";
import Button from "../../components/Button";
import Card from "../../components/Card";
import EmptyState from "../../components/EmptyState";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader from "../../components/PageHeader";
import StatCard from "../../components/StatCard";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";
import { getTimeUntil } from "../../utils/dateUtils";


export default function TutorDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  // Ensure API calls include authentication credentials
  const loadData = async () => {
    try {
      const [bookingsRes, sessionsRes] = await Promise.all([
        api.get("/bookings/user/my-bookings", { withCredentials: true }),
        api.get("/sessions?upcoming=true", { withCredentials: true }),
      ]);

      const bookings = bookingsRes.bookings || [];
      let sessions = sessionsRes.sessions || [];

      // If sessions are empty, derive from bookings
      if (sessions.length === 0) {
        sessions = bookings
          .filter((booking) => booking.assignedSessionId)
          .map((booking) => ({
            _id: booking.assignedSessionId,
            subjectId: booking.subjectId,
            studentId: booking.studentId,
            startAt: booking.preferredTimes[0]?.date,
          }));
      }

      setBookings(bookings);
      setSessions(sessions);

      // Calculate stats
      calculateStats(bookings, sessions);
    } catch (error) {
      console.error("Error loading dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (bookings, sessions) => {
    const pendingCount = bookings.filter(
      (b) =>
        b.status === "awaiting_tutor_confirmations" &&
        b.tutorConfirmations?.some(
          (tc) =>
            (typeof tc.tutorId === "object" ? tc.tutorId?._id : tc.tutorId) ===
            user?.id && tc.status === "pending"
        )
    ).length;

    const upcomingCount = sessions.length;
    const completedCount = bookings.filter(
      (b) => b.status === "confirmed"
    ).length;

    setStats({
      pending: pendingCount,
      upcoming: upcomingCount,
      completed: completedCount,
      rating: user?.profile?.rating || 0,
    });
  };

  const pendingBookings = bookings.filter(
    (b) =>
      b.status === "awaiting_tutor_confirmations" &&
      b.tutorConfirmations?.some(
        (tc) =>
          (typeof tc.tutorId === "object" ? tc.tutorId?._id : tc.tutorId) ===
          user?.id && tc.status === "pending"
      )
  );



  if (loading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title={`Welcome back, ${user?.name?.first}!`}
        subtitle="Here's what's happening with your tutoring sessions"
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Pending Requests"
            value={stats.pending}
            icon={<HiOutlineClock className="w-8 h-8 opacity-80" />}
            color="blue"
            subtitle="Awaiting your response"
          />
          <StatCard
            title="Upcoming Sessions"
            value={stats.upcoming}
            icon={<HiOutlineCalendar className="w-8 h-8 opacity-80" />}
            color="green"
            subtitle="Scheduled sessions"
          />
          <StatCard
            title="Completed"
            value={stats.completed}
            icon={<HiOutlineCheckCircle className="w-8 h-8 opacity-80" />}
            color="purple"
            subtitle="Total sessions"
          />
          <StatCard
            title="Your Rating"
            value={stats.rating > 0 ? stats.rating.toFixed(1) : "N/A"}
            icon={<HiStar className="w-8 h-8 opacity-80" />}
            color="yellow"
            subtitle="Average rating"
          />
        </div>
      )}

      {/* Quick Actions */}
      <Card className="mb-8">
        <h2 className="text-lg font-semibold text-text mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link
            to="/tutor/profile"
            className="flex flex-col items-center p-4 rounded-lg hover:bg-surface/70 transition-colors"
          >
            <HiOutlineUser className="w-8 h-8 text-primary mb-2" />
            <span className="text-sm font-medium text-text">Edit Profile</span>
          </Link>
          <Link
            to="/tutor/sessions"
            className="flex flex-col items-center p-4 rounded-lg hover:bg-surface/70 transition-colors"
          >
            <FaChalkboard className="w-8 h-8 text-primary mb-2" />
            <span className="text-sm font-medium text-text">All Sessions</span>
          </Link>
          {/* <Link
            to="/bookings"
            className="flex flex-col items-center p-4 rounded-lg hover:bg-surface/70 transition-colors"
          >
            <HiOutlineBookOpen className="w-8 h-8 text-primary mb-2" />
            <span className="text-sm font-medium text-text">All Bookings</span>
          </Link> */}
          <div className="flex flex-col items-center p-4 rounded-lg hover:bg-surface/70 transition-colors cursor-pointer">
            <HiOutlineChartBar className="w-8 h-8 text-primary mb-2" />
            <span className="text-sm font-medium text-text">Statistics</span>
          </div>
        </div>
      </Card>

      {/* Main Content */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Pending Requests */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-text">
              Pending Requests
            </h2>
            {pendingBookings.length > 0 && (
              <Badge label={`${pendingBookings.length} new`} variant="error" size="small" className="font-semibold" />
            )}
          </div>
          {pendingBookings.length === 0 ? (
            <EmptyState
              icon={HiOutlineCheckCircle}
              title="No pending requests"
              description="You're all caught up!"
            />
          ) : (
            <div className="space-y-4">
              {pendingBookings.map((booking) => (
                <Card key={booking._id}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg text-text">
                        {booking.subjectId?.name}
                      </h3>
                      <p className="text-sm text-text/70">
                        {booking.studentId?.name?.first}{" "}
                        {booking.studentId?.name?.last}
                      </p>
                    </div>
                    <Badge label="Pending" variant="warning" size="small" className="font-medium" />
                  </div>
                  <div className="flex items-center text-sm text-text/70 mb-4">
                    <HiOutlineClock className="w-4 h-4 mr-1" />
                    Expires {getTimeUntil(booking.expiresAt)}
                  </div>
                  <div className="mb-4 space-y-2">
                    <p className="text-sm text-text/80 flex items-center gap-2">
                      <strong>Type:</strong>
                      {booking.sessionType === "in-person" ? (
                        <>
                          <FaMapMarkerAlt className="flex-shrink-0" /> In-Person
                        </>
                      ) : (
                        <>
                          <FaVideo className="flex-shrink-0" /> Online (Google
                          Meet)
                        </>
                      )}
                    </p>
                    {booking.sessionType === "in-person" &&
                      booking.location && (
                        <p className="text-sm text-text/80">
                          <strong>Location:</strong> {booking.location}
                        </p>
                      )}
                  </div>
                  {booking.topicsToFocus && (
                    <div className="mb-4 p-3 bg-primary/10 rounded border border-primary/20">
                      <p className="text-xs font-semibold text-primary mb-2">
                        📋 Topics to Focus:
                      </p>
                      <p className="text-sm text-text whitespace-pre-wrap">
                        {booking.topicsToFocus}
                      </p>
                    </div>
                  )}
                  <Link
                    to={`/bookings/${booking._id}/tutors/${user?.id}/respond`}
                    className="block"
                  >
                    <Button className="w-full">Respond Now</Button>
                  </Link>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Sessions */}
        <div>
          <h2 className="text-xl font-semibold mb-4 text-text">
            Upcoming Sessions
          </h2>
          {sessions.length === 0 ? (
            <EmptyState
              icon={HiOutlineCalendar}
              title="No upcoming sessions"
              description="Your schedule is clear"
            />
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => (
                <Card key={session._id}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg text-text">
                        {session.subjectId?.name}
                      </h3>
                      <p className="text-sm text-text/70">
                        with {session.studentId?.name?.first}{" "}
                        {session.studentId?.name?.last}
                      </p>
                      <p className="text-xs text-text/60 mt-1 flex items-center gap-2">
                        {session.sessionType === "online" ? (
                          <>
                            <FaVideo className="flex-shrink-0 text-xs" /> Online
                          </>
                        ) : (
                          <>
                            <FaMapMarkerAlt className="flex-shrink-0 text-xs" />{" "}
                            In-Person
                          </>
                        )}
                        {session.sessionType === "in-person" &&
                          session.location &&
                          ` - ${session.location}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-text/70 mb-4">
                    <HiOutlineCalendar className="w-4 h-4 mr-1" />
                    {dayjs
                      .utc(session.startAt)
                      .local()
                      .format("MMM D, YYYY [at] h:mm A")}
                  </div>
                  <Link to={`/sessions/${session._id}`} className="block">
                    <Button variant="outline" className="w-full">
                      View Details
                    </Button>
                  </Link>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
